import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';

function BlogWhenRestFeelsUnsafe() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-warm-50">
      <Navigation variant="soft-brown" scrolled={true} />
      
      {/* SEO Meta Tags */}
      <title>When Rest Feels Unsafe | Blog - Yasi Philipos</title>
      <meta name="description" content="Understanding why rest feels unsafe and how nervous system conditioning affects our ability to slow down and be still." />
      
      {/* Article Schema for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": "When Rest Feels Unsafe",
          "author": {
            "@type": "Person",
            "name": "Yasi Philipos"
          },
          "datePublished": "2025-12-05",
          "description": "Understanding why rest feels unsafe and how nervous system conditioning affects our ability to slow down and be still."
        })}
      </script>
      
      {/* Article Header */}
      <section className="px-4 sm:px-6 lg:px-8 pt-32 pb-12">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center text-sage-600 hover:text-sage-800 mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Blog
          </Link>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-800 mb-6">
            When Rest Feels Unsafe
          </h1>
          
          <div className="flex items-center text-gray-600 text-sm mb-8">
            <span>By Yasi Philipos</span>
            <span className="mx-3">•</span>
            <time>December 2024</time>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg">
            
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              You've tried to rest... the silent retreats, the therapy, the journaling, the yoga, the vacations, the occasional (or not-so-occasional) night out with friends... and still, you can't fully let go. Even when you're technically "off," you can't stay silent or still for long enough.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Because the exhaustion you feel isn't just from your schedule, your job, or the people in your life... it's from years, and maybe generations, of conditioning.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Maybe the women in your family and community were the caretakers, the over-achievers... the ones who held everything together so no one else would fall apart.
            </p>

            <div className="bg-gradient-to-r from-sage-50 to-warm-50 border-l-4 border-sage-400 p-8 my-10 rounded-r-2xl">
              <p className="text-xl text-gray-800 leading-relaxed italic mb-0">
                Now, your nervous system equates rest with risk.<br />
                Stillness and silence feel unsafe.<br />
                Pushing through feels easier than slowing down.
              </p>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              And it's exhausting. You can feel it in your bones. You know it's time to tend to what's happening inside your body... to find ways to soften the pace.
            </p>

            <div className="bg-sage-50 p-8 rounded-xl border border-sage-200 my-10">
              <p className="text-xl text-gray-800 leading-relaxed mb-0">
                This isn't a flaw.<br />
                <strong className="text-sage-700">It's your body remembering how it learned to survive.</strong>
              </p>
            </div>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              Why Rest Feels Risky
            </h2>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              For many people-pleasers, rest wasn't modeled as safe. You may have grown up watching:
            </p>

            <ul className="space-y-4 my-8">
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700">Parents who never stopped moving, never sat still, never seemed to need anything</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700">Caregivers who earned love and belonging through service and sacrifice</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700">Family members who wore their exhaustion like a badge of honor</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-sage-600 mt-1">•</span>
                <span className="text-gray-700">Cultural messages that rest is lazy, selfish, or indulgent</span>
              </li>
            </ul>

            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Your nervous system internalized these messages. It learned: <strong>staying useful keeps you safe. Slowing down means losing your worth.</strong>
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              The Body Keeps the Score
            </h2>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Even when you know intellectually that you deserve rest, your body tells a different story. You might experience:
            </p>

            <ul className="space-y-3 my-8">
              <li className="flex items-start gap-3">
                <span className="text-warm-600 mt-1">•</span>
                <span className="text-gray-700">Anxiety when you try to slow down</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-warm-600 mt-1">•</span>
                <span className="text-gray-700">Guilt when you're not being "productive"</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-warm-600 mt-1">•</span>
                <span className="text-gray-700">An inability to sit with stillness without reaching for your phone</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-warm-600 mt-1">•</span>
                <span className="text-gray-700">Physical discomfort or restlessness when you pause</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-warm-600 mt-1">•</span>
                <span className="text-gray-700">A constant mental to-do list that never quiets</span>
              </li>
            </ul>

            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              This isn't willpower. This isn't a personal failing. This is your nervous system doing exactly what it was trained to do.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">
              Teaching Your Body That Rest Is Safe
            </h2>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The path to reclaiming rest isn't about forcing yourself to relax. It's about slowly, gently teaching your nervous system that you're safe—even when you're not "doing" anything.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              This work happens through:
            </p>

            <div className="bg-gradient-to-br from-sage-50 to-warm-50 p-8 rounded-2xl my-10 border border-sage-200">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <span className="text-sage-600 text-xl">✓</span>
                  <span className="text-lg text-gray-700"><strong>Nervous system regulation</strong> — Learning to track your body's signals and build capacity for rest</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-sage-600 text-xl">✓</span>
                  <span className="text-lg text-gray-700"><strong>Somatic practices</strong> — Reconnecting with your body in gentle, safe ways</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-sage-600 text-xl">✓</span>
                  <span className="text-lg text-gray-700"><strong>Parts work</strong> — Honoring the parts of you that learned to push through while inviting them to soften</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-sage-600 text-xl">✓</span>
                  <span className="text-lg text-gray-700"><strong>Co-regulation</strong> — Experiencing safety in relationship, where rest is witnessed and honored</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-r from-warm-50 to-sage-50 border-l-4 border-warm-500 p-8 my-12 rounded-r-2xl">
              <p className="text-2xl text-gray-800 leading-relaxed font-light mb-0">
                This work is for the woman who's tired of holding it all together.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mt-4 mb-0">
                The one who's ready to soften, to rest, and to let safety live in her body again... gently and without guilt.
              </p>
            </div>

            {/* Call to Action */}
            <div className="mt-16 p-8 bg-gradient-to-br from-sage-50 to-warm-50 rounded-2xl text-center border border-sage-200">
              <h3 className="text-2xl font-serif font-light text-sage-800 mb-4">
                Ready to Begin?
              </h3>
              <p className="text-gray-700 mb-6 max-w-xl mx-auto leading-relaxed">
                Let's work together to help your nervous system feel safe to rest.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/services/individual-sessions"
                  className="inline-block px-8 py-3 bg-sage-600 text-white rounded-lg font-medium hover:bg-sage-700 transition-colors"
                >
                  Explore Individual Sessions
                </Link>
                <Link
                  to="/services/group-coaching"
                  className="inline-block px-8 py-3 bg-white text-sage-700 border-2 border-sage-300 rounded-lg font-medium hover:bg-sage-50 transition-colors"
                >
                  Join Group Coaching
                </Link>
              </div>
            </div>

            {/* Author Bio */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-sage-100 flex items-center justify-center">
                    <span className="text-2xl text-sage-600">YP</span>
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-2">Yasi Philipos</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Licensed MFT helping women break cycles of people-pleasing and reclaim rest through nervous system regulation and somatic healing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default BlogWhenRestFeelsUnsafe;
